import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import { AdminSuccessBanner } from '@/components/shared/AdminSuccessBanner';
import { useAdminSearchUsers } from '@/hooks/useAdminUsers';
import {
    ArrowLeft,
    Loader2,
    Save,
    Search,
    UserPlus,
    UserMinus,
    Crown,
    RefreshCw,
    Check,
    XCircle,
    Megaphone,
} from 'lucide-react';
import {
    useAdminFishingClubDetail,
    useAdminFishingClubUpdate,
    useAdminFishingClubMembers,
    useAdminFishingClubMemberAdd,
    useAdminFishingClubMemberRemove,
    useAdminFishingClubMemberSetRole,
    useAdminFishingClubSetStatus,
    useAdminFishingClubDelete,
    useAdminFishingClubAnnouncementPost,
    type AdminClubDetail,
    type AdminClubAnnouncementValidityDays,
    type ClubMemberRole,
} from './hooks/useFishingClubsAdmin';

const ROLE_LABELS: Record<string, string> = {
    creator: 'Creador',
    admin: 'Admin',
    member: 'Socio',
};

const ANNOUNCEMENT_VALIDITY_OPTIONS: { days: AdminClubAnnouncementValidityDays; label: string }[] = [
    { days: 1, label: '1 día' },
    { days: 3, label: '3 días' },
    { days: 5, label: '5 días' },
    { days: 15, label: '15 días' },
    { days: 30, label: '1 mes' },
    { days: 90, label: '3 meses' },
];

function toggleTag(list: string[], tag: string): string[] {
    return list.includes(tag) ? list.filter((t) => t !== tag) : [...list, tag];
}

function ClubAnnouncementSection({
    clubId,
    clubStatus,
}: {
    clubId: string;
    clubStatus: string;
}) {
    const postMut = useAdminFishingClubAnnouncementPost();
    const [draft, setDraft] = useState('');
    const [validityDays, setValidityDays] = useState<AdminClubAnnouncementValidityDays>(15);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const canPublish = clubStatus === 'approved';

    const publish = () => {
        const body = draft.trim();
        setError(null);
        setSuccessMessage(null);
        if (!canPublish) {
            setError('Solo se pueden publicar anuncios en clubes aprobados');
            return;
        }
        if (!body) {
            setError('Escribe el texto del anuncio');
            return;
        }
        postMut.mutate(
            { clubId, body, validityDays },
            {
                onSuccess: () => {
                    setDraft('');
                    setValidityDays(15);
                    setSuccessMessage(
                        'Anuncio publicado; los miembros lo verán en General y recibirán notificación'
                    );
                },
                onError: (e) =>
                    setError(e instanceof Error ? e.message : 'No se pudo publicar el anuncio'),
            }
        );
    };

    return (
        <div className="rounded-xl border border-border bg-card p-4 sm:p-5 space-y-4">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <Megaphone className="w-4 h-4 text-cyan-400 shrink-0" aria-hidden />
                    <div className="min-w-0">
                        <h2 className="text-sm font-semibold text-foreground">Publicar anuncio</h2>
                        <p className="text-xs text-muted-foreground">
                            Aviso a todos los socios del club (sin necesidad de ser miembro)
                        </p>
                    </div>
                </div>
                <Button size="sm" onClick={publish} disabled={postMut.isPending || !canPublish}>
                    {postMut.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Megaphone className="w-4 h-4" />
                    )}
                    <span className="ml-1.5">Publicar</span>
                </Button>
            </div>

            <AdminSuccessBanner message={successMessage} onDismiss={() => setSuccessMessage(null)} />
            {error && <p className="text-sm text-destructive">{error}</p>}
            {!canPublish && (
                <p className="text-xs text-amber-400">
                    El club debe estar aprobado para poder publicar anuncios.
                </p>
            )}

            <div className="space-y-3">
                <div>
                    <Label htmlFor="club-announcement-body">Texto del anuncio</Label>
                    <Textarea
                        id="club-announcement-body"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        maxLength={4000}
                        rows={4}
                        placeholder="Fechas, reglas, novedades…"
                        disabled={!canPublish || postMut.isPending}
                    />
                    <p className="mt-1 text-xs text-muted-foreground text-right">
                        {draft.trim().length}/4000
                    </p>
                </div>
                <div>
                    <Label htmlFor="club-announcement-validity">Vigencia</Label>
                    <select
                        id="club-announcement-validity"
                        className="mt-1 flex h-9 w-full max-w-xs rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                        value={validityDays}
                        onChange={(e) =>
                            setValidityDays(Number(e.target.value) as AdminClubAnnouncementValidityDays)
                        }
                        disabled={!canPublish || postMut.isPending}
                    >
                        {ANNOUNCEMENT_VALIDITY_OPTIONS.map((opt) => (
                            <option key={opt.days} value={opt.days}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
}

function ClubInfoForm({
    club,
    onSaved,
}: {
    club: AdminClubDetail;
    onSaved?: () => void;
}) {
    const updateMut = useAdminFishingClubUpdate();
    const [form, setForm] = useState({
        name: club.name,
        initials: club.initials,
        description: club.description,
        external_url: club.external_url,
        logo_url: club.logo_url ?? '',
        country_code: club.country_code,
        location_label: club.location_label,
        latitude: club.latitude != null ? String(club.latitude) : '',
        longitude: club.longitude != null ? String(club.longitude) : '',
        visibility: club.visibility === 'secret' ? 'secret' : 'public',
        verified: club.verified,
        water_types: club.water_types,
        modalities: club.modalities,
        location_slugs: club.location_slugs,
    });
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    useEffect(() => {
        setForm({
            name: club.name,
            initials: club.initials,
            description: club.description,
            external_url: club.external_url,
            logo_url: club.logo_url ?? '',
            country_code: club.country_code,
            location_label: club.location_label,
            latitude: club.latitude != null ? String(club.latitude) : '',
            longitude: club.longitude != null ? String(club.longitude) : '',
            visibility: club.visibility === 'secret' ? 'secret' : 'public',
            verified: club.verified,
            water_types: club.water_types,
            modalities: club.modalities,
            location_slugs: club.location_slugs,
        });
    }, [club]);

    const save = () => {
        setError(null);
        setSuccessMessage(null);
        if (!form.name.trim() || !form.initials.trim()) {
            setError('Nombre y siglas son obligatorios');
            return;
        }
        const lat = form.latitude.trim() === '' ? null : Number(form.latitude);
        const lng = form.longitude.trim() === '' ? null : Number(form.longitude);
        if (lat != null && Number.isNaN(lat)) {
            setError('Latitud no válida');
            return;
        }
        if (lng != null && Number.isNaN(lng)) {
            setError('Longitud no válida');
            return;
        }

        updateMut.mutate(
            {
                id: club.id,
                name: form.name.trim(),
                description: form.description.trim() || 'Club',
                external_url: form.external_url.trim(),
                logo_url: form.logo_url.trim(),
                country_code: form.country_code.trim().toUpperCase() || 'ES',
                location_label: form.location_label.trim(),
                latitude: lat,
                longitude: lng,
                visibility: form.visibility,
                water_types: form.water_types.length ? form.water_types : ['dulce'],
                modalities: form.modalities.length ? form.modalities : ['spinning'],
                location_slugs: form.location_slugs.length ? form.location_slugs : ['orilla'],
                initials: form.initials.trim().toUpperCase().slice(0, 5),
                verified: form.verified,
            },
            {
                onSuccess: () => {
                    setSuccessMessage('Cambios guardados correctamente');
                    onSaved?.();
                },
                onError: (e) => setError(e instanceof Error ? e.message : 'Error al guardar'),
            }
        );
    };

    return (
        <div className="rounded-xl border border-border bg-card p-4 sm:p-5 space-y-4">
            <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-foreground">Información del club</h2>
                <Button size="sm" onClick={save} disabled={updateMut.isPending}>
                    {updateMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    <span className="ml-1.5">Guardar</span>
                </Button>
            </div>

            <AdminSuccessBanner message={successMessage} onDismiss={() => setSuccessMessage(null)} />
            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                    <Label>Nombre</Label>
                    <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                </div>
                <div>
                    <Label>Siglas (1–5)</Label>
                    <Input
                        maxLength={5}
                        value={form.initials}
                        onChange={(e) =>
                            setForm((f) => ({ ...f, initials: e.target.value.toUpperCase().slice(0, 5) }))
                        }
                    />
                </div>
                <div>
                    <Label>Visibilidad</Label>
                    <select
                        className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                        value={form.visibility}
                        onChange={(e) => setForm((f) => ({ ...f, visibility: e.target.value }))}
                    >
                        <option value="public">Público</option>
                        <option value="secret">Secreto</option>
                    </select>
                </div>
                <div className="sm:col-span-2">
                    <Label>Descripción</Label>
                    <Textarea
                        rows={3}
                        value={form.description}
                        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    />
                </div>
                <div>
                    <Label>URL externa</Label>
                    <Input
                        value={form.external_url}
                        onChange={(e) => setForm((f) => ({ ...f, external_url: e.target.value }))}
                    />
                </div>
                <div>
                    <Label>Logo URL</Label>
                    <Input
                        value={form.logo_url}
                        onChange={(e) => setForm((f) => ({ ...f, logo_url: e.target.value }))}
                        placeholder="https://…"
                    />
                </div>
                <div>
                    <Label>País (ISO2)</Label>
                    <Input
                        value={form.country_code}
                        onChange={(e) =>
                            setForm((f) => ({ ...f, country_code: e.target.value.toUpperCase().slice(0, 2) }))
                        }
                    />
                </div>
                <div>
                    <Label>Ubicación</Label>
                    <Input
                        value={form.location_label}
                        onChange={(e) => setForm((f) => ({ ...f, location_label: e.target.value }))}
                    />
                </div>
                <div>
                    <Label>Latitud</Label>
                    <Input
                        value={form.latitude}
                        onChange={(e) => setForm((f) => ({ ...f, latitude: e.target.value }))}
                    />
                </div>
                <div>
                    <Label>Longitud</Label>
                    <Input
                        value={form.longitude}
                        onChange={(e) => setForm((f) => ({ ...f, longitude: e.target.value }))}
                    />
                </div>
            </div>

            <div className="flex flex-wrap gap-4 pt-1">
                <label className="flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={form.verified}
                        onChange={(e) => setForm((f) => ({ ...f, verified: e.target.checked }))}
                    />
                    Verificado
                </label>
                <label className="flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={form.water_types.includes('dulce')}
                        onChange={() =>
                            setForm((f) => ({ ...f, water_types: toggleTag(f.water_types, 'dulce') }))
                        }
                    />
                    Agua dulce
                </label>
                <label className="flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={form.water_types.includes('salada')}
                        onChange={() =>
                            setForm((f) => ({ ...f, water_types: toggleTag(f.water_types, 'salada') }))
                        }
                    />
                    Agua salada
                </label>
            </div>
        </div>
    );
}

function MembersSection({ clubId }: { clubId: string }) {
    const [search, setSearch] = useState('');
    const [addOpen, setAddOpen] = useState(false);
    const [userQuery, setUserQuery] = useState('');
    const [addRole, setAddRole] = useState<'member' | 'admin'>('member');
    const [removeUserId, setRemoveUserId] = useState<string | null>(null);
    const [transferUserId, setTransferUserId] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);

    const { data: members = [], isLoading, refetch } = useAdminFishingClubMembers(clubId, search);
    const { data: searchResults = [], isFetching: searching } = useAdminSearchUsers(userQuery, {
        searchByEmail: true,
        limit: 12,
    });
    const addMut = useAdminFishingClubMemberAdd();
    const removeMut = useAdminFishingClubMemberRemove();
    const setRoleMut = useAdminFishingClubMemberSetRole();

    const memberIds = new Set(members.map((m) => m.user_id));
    const candidates = searchResults.filter((u) => !memberIds.has(u.user_id));

    const changeRole = (userId: string, role: ClubMemberRole) => {
        setActionError(null);
        if (role === 'creator') {
            setTransferUserId(userId);
            return;
        }
        setRoleMut.mutate(
            { clubId, userId, role },
            { onError: (e) => setActionError(e instanceof Error ? e.message : 'Error al cambiar rol') }
        );
    };

    return (
        <div className="rounded-xl border border-border bg-card p-4 sm:p-5 space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h2 className="text-sm font-semibold text-foreground">Miembros</h2>
                    <p className="text-xs text-muted-foreground">
                        {members[0]?.member_total ?? members.length} socios
                        {' · '}
                        Usa <span className="text-foreground/80">Hacer creador</span> para transferir la propiedad
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => refetch()}>
                        <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button size="sm" onClick={() => setAddOpen(true)}>
                        <UserPlus className="w-4 h-4 mr-1" />
                        Añadir
                    </Button>
                </div>
            </div>

            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                    className="pl-9"
                    placeholder="Filtrar miembros…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            {actionError && <p className="text-sm text-destructive">{actionError}</p>}

            {isLoading ? (
                <p className="text-muted-foreground text-sm">Cargando miembros…</p>
            ) : members.length === 0 ? (
                <p className="text-muted-foreground text-sm">Sin miembros</p>
            ) : (
                <div className="rounded-lg border border-border overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                                <tr>
                                    <th className="px-3 py-2 font-medium">Usuario</th>
                                    <th className="px-3 py-2 font-medium">Rol</th>
                                    <th className="px-3 py-2 font-medium hidden sm:table-cell">Alta</th>
                                    <th className="px-3 py-2 font-medium text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {members.map((m) => (
                                    <tr key={m.user_id} className="border-t border-border">
                                        <td className="px-3 py-2.5">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <div className="w-8 h-8 rounded-full bg-muted overflow-hidden shrink-0 flex items-center justify-center text-xs font-bold">
                                                    {m.avatar_url ? (
                                                        <img
                                                            src={m.avatar_url}
                                                            alt=""
                                                            className="w-full h-full object-cover"
                                                            referrerPolicy="no-referrer"
                                                        />
                                                    ) : (
                                                        (m.display_name || m.username || '?')[0]?.toUpperCase()
                                                    )}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-medium truncate">
                                                        {m.display_name || m.username || 'Sin nombre'}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground truncate">
                                                        @{m.username || '—'}
                                                        {m.email ? ` · ${m.email}` : ''}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {m.role === 'creator' ? (
                                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-500">
                                                    <Crown className="w-3.5 h-3.5" />
                                                    {ROLE_LABELS.creator}
                                                </span>
                                            ) : (
                                                <select
                                                    className="h-8 rounded-md border border-input bg-background px-2 text-xs max-w-[160px]"
                                                    value={m.role}
                                                    disabled={setRoleMut.isPending}
                                                    onChange={(e) =>
                                                        changeRole(m.user_id, e.target.value as ClubMemberRole)
                                                    }
                                                >
                                                    <option value="admin">{ROLE_LABELS.admin}</option>
                                                    <option value="member">{ROLE_LABELS.member}</option>
                                                    <option value="creator">Creador (transferir)</option>
                                                </select>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 text-xs text-muted-foreground hidden sm:table-cell whitespace-nowrap">
                                            {m.joined_at
                                                ? new Date(m.joined_at).toLocaleDateString('es-ES')
                                                : '—'}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <div className="flex justify-end flex-wrap gap-1">
                                                {m.role !== 'creator' && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="text-amber-600 border-amber-500/40 hover:bg-amber-500/10 hover:text-amber-500"
                                                        onClick={() => setTransferUserId(m.user_id)}
                                                    >
                                                        <Crown className="w-4 h-4" />
                                                        <span className="ml-1">Hacer creador</span>
                                                    </Button>
                                                )}
                                                {m.role !== 'creator' && (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="text-destructive"
                                                        title="Expulsar"
                                                        onClick={() => setRemoveUserId(m.user_id)}
                                                    >
                                                        <UserMinus className="w-4 h-4" />
                                                        <span className="ml-1 hidden sm:inline">Expulsar</span>
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Añadir miembro</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Buscar usuario</Label>
                            <Input
                                placeholder="Nombre o email…"
                                value={userQuery}
                                onChange={(e) => setUserQuery(e.target.value)}
                            />
                        </div>
                        <div>
                            <Label>Rol inicial</Label>
                            <select
                                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                                value={addRole}
                                onChange={(e) => setAddRole(e.target.value as 'member' | 'admin')}
                            >
                                <option value="member">Socio</option>
                                <option value="admin">Admin</option>
                            </select>
                        </div>
                        <div className="max-h-56 overflow-y-auto rounded-lg border border-border divide-y divide-border">
                            {userQuery.trim().length < 1 ? (
                                <p className="p-3 text-sm text-muted-foreground">Escribe para buscar…</p>
                            ) : searching ? (
                                <p className="p-3 text-sm text-muted-foreground flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" /> Buscando…
                                </p>
                            ) : candidates.length === 0 ? (
                                <p className="p-3 text-sm text-muted-foreground">Sin resultados</p>
                            ) : (
                                candidates.map((u) => (
                                    <button
                                        key={u.user_id}
                                        type="button"
                                        className="w-full text-left px-3 py-2.5 hover:bg-muted/60 transition-colors"
                                        disabled={addMut.isPending}
                                        onClick={() => {
                                            setActionError(null);
                                            addMut.mutate(
                                                { clubId, userId: u.user_id, role: addRole },
                                                {
                                                    onSuccess: () => {
                                                        setAddOpen(false);
                                                        setUserQuery('');
                                                    },
                                                    onError: (e) =>
                                                        setActionError(
                                                            e instanceof Error ? e.message : 'Error al añadir'
                                                        ),
                                                }
                                            );
                                        }}
                                    >
                                        <p className="text-sm font-medium">
                                            {u.display_name || 'Sin nombre'}
                                        </p>
                                        <p className="text-xs text-muted-foreground truncate">
                                            {u.email || u.user_id}
                                        </p>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddOpen(false)}>
                            Cerrar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AdminConfirmDialog
                open={!!removeUserId}
                variant="danger"
                title="Expulsar miembro"
                description={<p>¿Expulsar a este usuario del club?</p>}
                confirmLabel="Expulsar"
                onCancel={() => setRemoveUserId(null)}
                onConfirm={() => {
                    if (!removeUserId) return;
                    setActionError(null);
                    removeMut.mutate(
                        { clubId, userId: removeUserId },
                        {
                            onSuccess: () => setRemoveUserId(null),
                            onError: (e) => {
                                setActionError(e instanceof Error ? e.message : 'Error al expulsar');
                                setRemoveUserId(null);
                            },
                        }
                    );
                }}
                confirmDisabled={removeMut.isPending}
            />

            <AdminConfirmDialog
                open={!!transferUserId}
                variant="default"
                title="Transferir propiedad"
                description={
                    <p>
                        El creador actual pasará a <strong>Admin</strong> y este usuario será el nuevo{' '}
                        <strong>Creador</strong> del club. ¿Continuar?
                    </p>
                }
                confirmLabel="Transferir"
                onCancel={() => setTransferUserId(null)}
                onConfirm={() => {
                    if (!transferUserId) return;
                    setActionError(null);
                    setRoleMut.mutate(
                        { clubId, userId: transferUserId, role: 'creator' },
                        {
                            onSuccess: () => setTransferUserId(null),
                            onError: (e) => {
                                setActionError(e instanceof Error ? e.message : 'Error al transferir');
                                setTransferUserId(null);
                            },
                        }
                    );
                }}
                confirmDisabled={setRoleMut.isPending}
            />
        </div>
    );
}

export function ClubDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { data: club, isLoading, error, refetch } = useAdminFishingClubDetail(id ?? null);
    const setStatus = useAdminFishingClubSetStatus();
    const del = useAdminFishingClubDelete();
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');

    if (!id) {
        return <p className="text-muted-foreground">Club no válido</p>;
    }

    if (isLoading) {
        return (
            <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
                <Loader2 className="w-5 h-5 animate-spin" />
                Cargando club…
            </div>
        );
    }

    if (error || !club) {
        return (
            <div className="space-y-4">
                <Button variant="outline" size="sm" onClick={() => navigate('/clubs')}>
                    <ArrowLeft className="w-4 h-4 mr-1" /> Volver
                </Button>
                <p className="text-destructive">
                    {error instanceof Error ? error.message : 'Club no encontrado'}
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-3">
                <Button variant="ghost" size="sm" className="w-fit -ml-2" asChild>
                    <Link to="/clubs">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Clubes
                    </Link>
                </Button>
                <PageHeader
                    title={`[${club.initials}] ${club.name}`}
                    description={`@${club.creator_username || '—'} · ${club.visibility} · ${club.status} · ${club.members_count} miembros`}
                >
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={() => refetch()}>
                            <RefreshCw className="w-4 h-4" />
                        </Button>
                        {club.status === 'pending' && (
                            <>
                                <Button
                                    size="sm"
                                    onClick={() => setStatus.mutate({ id: club.id, status: 'approved' })}
                                >
                                    <Check className="w-4 h-4 mr-1" /> Aprobar
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setRejectOpen(true)}>
                                    <XCircle className="w-4 h-4 mr-1" /> Rechazar
                                </Button>
                            </>
                        )}
                        {club.status === 'rejected' && (
                            <Button
                                size="sm"
                                onClick={() => setStatus.mutate({ id: club.id, status: 'approved' })}
                            >
                                Aprobar
                            </Button>
                        )}
                        <Button size="sm" variant="destructive" onClick={() => setDeleteOpen(true)}>
                            Eliminar
                        </Button>
                    </div>
                </PageHeader>
            </div>

            {club.logo_url && (
                <div className="flex items-center gap-3">
                    <img
                        src={club.logo_url}
                        alt=""
                        className="w-14 h-14 rounded-xl object-cover border border-border"
                        referrerPolicy="no-referrer"
                    />
                    {club.verified && (
                        <span className="text-xs text-violet-500 font-medium">Club verificado</span>
                    )}
                </div>
            )}

            <ClubInfoForm club={club} />
            <ClubAnnouncementSection clubId={club.id} clubStatus={club.status} />
            <MembersSection clubId={club.id} />

            <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Rechazar club</DialogTitle>
                    </DialogHeader>
                    <Textarea
                        placeholder="Motivo"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                    />
                    <DialogFooter>
                        <Button
                            onClick={() => {
                                setStatus.mutate(
                                    {
                                        id: club.id,
                                        status: 'rejected',
                                        rejectionReason: rejectReason || '—',
                                    },
                                    {
                                        onSuccess: () => {
                                            setRejectOpen(false);
                                            setRejectReason('');
                                            refetch();
                                        },
                                    }
                                );
                            }}
                        >
                            Confirmar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AdminConfirmDialog
                open={deleteOpen}
                variant="danger"
                title="Eliminar club"
                description={<p>¿Eliminar este club? Esta acción no se puede deshacer.</p>}
                confirmLabel="Eliminar"
                onCancel={() => setDeleteOpen(false)}
                onConfirm={() => {
                    del.mutate(club.id, {
                        onSuccess: () => navigate('/clubs'),
                    });
                    setDeleteOpen(false);
                }}
                confirmDisabled={del.isPending}
            />
        </div>
    );
}
